import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, RotateCcw } from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';
import { formatUZS } from '@/lib/format';
import { PAYOUT_STATUSES, type MerchantPayout, type PayoutStatus } from '@/lib/payouts';
import { PayoutBadge } from '@/components/payouts/PayoutBadge';
import { intlLocale } from './adminLocale';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const payoutsKey = (status: PayoutStatus | 'all') => ['admin-merchant-payouts', status] as const;

/** T232: Hisob MMT → merchant payouts (murabaha step 7) with a manual retry for a failed or stuck one. */
export default function AdminMerchantPayouts() {
  const { t } = useTranslation();
  const { hasRole } = useAdminAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<PayoutStatus | 'all'>('all');
  const [busyId, setBusyId] = useState<number | null>(null);
  const canRetry = hasRole('platform_owner', 'finance_admin');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: payoutsKey(status),
    refetchInterval: 30000,
    queryFn: async (): Promise<MerchantPayout[]> => {
      const query = status === 'all' ? {} : { status };
      const { data, error } = await apiClient.GET('/admin/merchant-payouts', { params: { query: query as never } });
      if (error) throw error;
      return (data as unknown as { data: MerchantPayout[] }).data;
    },
  });

  const retry = async (id: number) => {
    setBusyId(id);
    try {
      const { error } = await apiClient.POST('/admin/merchant-payouts/{payout}/retry', { params: { path: { payout: id } } });
      if (error) throw error;
      toast.success(t('adm_payouts.retry_done'));
      qc.invalidateQueries({ queryKey: ['admin-merchant-payouts'] });
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground">{t('adm_payouts.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('adm_payouts.subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={(v) => setStatus(v as PayoutStatus | 'all')}>
          <SelectTrigger className="w-[170px]" aria-label={t('adm_payouts.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('adm_payouts.all_statuses')}</SelectItem>
            {PAYOUT_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`mur_payout.status.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr className="border-b border-border text-left">
              <th className="px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">{t('adm_payouts.merchant')}</th>
              <th className="px-4 py-3 font-medium">{t('adm_payouts.case')}</th>
              <th className="px-4 py-3 font-medium">{t('adm_payouts.invoice')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('adm_payouts.amount')}</th>
              <th className="px-4 py-3 font-medium">{t('adm_payouts.status')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('adm_payouts.attempts')}</th>
              <th className="px-4 py-3 font-medium">{t('adm_payouts.last_error')}</th>
              <th className="px-4 py-3 font-medium">{t('adm_payouts.paid_at')}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={16} /></td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">{t('adm_payouts.empty')}</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 text-muted-foreground">{p.id}</td>
                <td className="px-4 py-3">{p.merchant?.name ?? '—'}</td>
                <td className="px-4 py-3">{p.murabaha_case_id}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.invoice_doc_num ?? '—'}</td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatUZS(Number(p.amount))}</td>
                <td className="px-4 py-3"><PayoutBadge status={p.status} /></td>
                <td className="px-4 py-3 text-right">{p.attempts}</td>
                <td className="px-4 py-3 text-xs text-destructive max-w-[240px] break-words">{p.last_error ?? '—'}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{p.paid_at ? new Date(p.paid_at).toLocaleString(intlLocale()) : '—'}</td>
                <td className="px-4 py-3 text-right">
                  {canRetry && p.status !== 'paid' && (
                    <Button size="sm" variant="outline" disabled={busyId === p.id} onClick={() => retry(p.id)}>
                      {busyId === p.id ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RotateCcw size={14} className="mr-1.5" />}
                      {t('adm_payouts.retry')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
